import React, { useState } from 'react';
import { Button } from '../ui/button';
import { MessageSquare, User, Users, CheckCircle2, XCircle, Volume2, ChevronRight } from 'lucide-react';
import { useSounds } from '@/hooks/useSounds';

// İnteraktif Diyalog Simülasyonu - Rol yapma oyunu
export default function DialogueSimulation({ title, scenario, dialogues, onAnswer }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedOption, setSelectedOption] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [score, setScore] = useState(0);
  const [completed, setCompleted] = useState(false);
  const { playCorrect, playIncorrect, playClick, playLessonComplete } = useSounds();

  const currentDialogue = dialogues[currentStep];
  const isLastStep = currentStep === dialogues.length - 1;

  const handleSelect = (optionIndex) => {
    playClick();
    setSelectedOption(optionIndex);
    setShowResult(true);
    
    if (optionIndex === currentDialogue.correctAnswer) {
      playCorrect();
      setScore(prev => prev + 1);
    } else {
      playIncorrect();
    }
  };

  const handleNext = () => {
    if (isLastStep) {
      setCompleted(true);
      playLessonComplete();
    } else {
      setCurrentStep(prev => prev + 1);
      setSelectedOption(null);
      setShowResult(false);
    }
  };

  const handleComplete = () => {
    const passed = score >= dialogues.length * 0.6;
    if (onAnswer) onAnswer(passed, passed ? 1 : 0);
  };

  const speakText = (text) => {
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'en-US';
      utterance.rate = 0.85;
      window.speechSynthesis.speak(utterance);
    }
  };

  if (completed) {
    return (
      <div className="w-full max-w-2xl mx-auto text-center">
        <div className="bg-white rounded-3xl shadow-xl p-8 mb-6">
          <div className={`w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-4 ${
            score >= dialogues.length * 0.6 ? 'bg-green-100' : 'bg-red-100'
          }`}>
            {score >= dialogues.length * 0.6 ? (
              <CheckCircle2 className="w-10 h-10 text-green-500" />
            ) : (
              <XCircle className="w-10 h-10 text-red-500" />
            )}
          </div>
          <h3 className="text-2xl font-bold text-gray-800 mb-2">
            {score >= dialogues.length * 0.6 ? 'Harika! 🎉' : 'Tekrar Dene!'}
          </h3>
          <p className="text-gray-600 mb-4">
            {score} / {dialogues.length} doğru cevap
          </p>
          <div className="bg-gray-100 rounded-xl p-4 mb-6">
            <p className="text-sm text-gray-600">
              {score >= dialogues.length * 0.6 
                ? 'Diyalog becerilerin gelişiyor! Yolcularla iletişim kurabilirsin.' 
                : 'Biraz daha pratik yapmaya ihtiyacın var. Dersi tekrar et!'}
            </p>
          </div>
        </div>
        <Button
          onClick={handleComplete}
          className="w-full py-4 text-lg font-bold bg-emerald-500 hover:bg-emerald-600 rounded-xl"
        >
          Devam Et
          <ChevronRight className="w-5 h-5 ml-2" />
        </Button>
      </div>
    );
  }

  return (
    <div className="w-full max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-indigo-100 rounded-xl">
            <MessageSquare className="w-6 h-6 text-indigo-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-800">{title}</h2>
            <p className="text-sm text-gray-500">Diyalog Simülasyonu</p>
          </div>
        </div>
        <div className="text-sm font-medium text-gray-500">
          {currentStep + 1} / {dialogues.length}
        </div>
      </div>

      {/* Scenario Box */}
      {scenario && currentStep === 0 && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 mb-6">
          <p className="text-sm text-indigo-800">
            🎬 <strong>Senaryo:</strong> {scenario}
          </p>
        </div>
      )}

      {/* Progress */}
      <div className="w-full bg-gray-200 rounded-full h-2 mb-6">
        <div 
          className="bg-indigo-500 h-2 rounded-full transition-all duration-300"
          style={{ width: `${((currentStep + 1) / dialogues.length) * 100}%` }}
        />
      </div>

      {/* Dialogue Card */}
      <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden mb-6">
        {/* Other Person's Message */}
        <div className="bg-gray-50 p-4 border-b">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
              <User className="w-5 h-5 text-blue-600" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-blue-600 mb-1">
                {currentDialogue.speaker || 'Yolcu'}
              </p>
              <p className="text-gray-800">{currentDialogue.message}</p>
              {currentDialogue.messageTr && (
                <p className="text-sm text-gray-500 mt-1 italic">{currentDialogue.messageTr}</p>
              )}
            </div>
            <button
              onClick={() => speakText(currentDialogue.message)}
              className="p-2 hover:bg-gray-200 rounded-full"
            >
              <Volume2 className="w-4 h-4 text-blue-500" />
            </button>
          </div>
        </div>

        {/* Your Response Options */}
        <div className="p-4">
          <p className="text-sm font-medium text-gray-600 mb-3 flex items-center gap-2">
            <Users className="w-4 h-4" />
            Sen (Kabin Memuru) - Ne dersin?
          </p>
          
          <div className="space-y-3">
            {currentDialogue.options.map((option, idx) => {
              let buttonStyle = 'bg-gray-50 hover:bg-gray-100 border-gray-200';
              
              if (showResult) {
                if (idx === currentDialogue.correctAnswer) {
                  buttonStyle = 'bg-green-50 border-green-500 text-green-700';
                } else if (idx === selectedOption) {
                  buttonStyle = 'bg-red-50 border-red-500 text-red-700';
                }
              } else if (selectedOption === idx) {
                buttonStyle = 'bg-indigo-50 border-indigo-500';
              }

              return (
                <button
                  key={idx}
                  onClick={() => !showResult && handleSelect(idx)}
                  disabled={showResult}
                  className={`w-full p-4 rounded-xl border-2 text-left transition-all ${buttonStyle}`}
                >
                  <div className="flex items-center justify-between">
                    <span>{option.text}</span>
                    {showResult && idx === currentDialogue.correctAnswer && (
                      <CheckCircle2 className="w-5 h-5 text-green-500" />
                    )}
                    {showResult && idx === selectedOption && idx !== currentDialogue.correctAnswer && (
                      <XCircle className="w-5 h-5 text-red-500" />
                    )}
                  </div>
                  {option.textTr && (
                    <p className="text-sm text-gray-500 mt-1">{option.textTr}</p>
                  )}
                </button>
              );
            })}
          </div>

          {/* Feedback */}
          {showResult && (
            <div className={`mt-4 p-4 rounded-xl ${
              selectedOption === currentDialogue.correctAnswer 
                ? 'bg-green-50 border border-green-200' 
                : 'bg-red-50 border border-red-200'
            }`}>
              <p className={`font-medium ${
                selectedOption === currentDialogue.correctAnswer ? 'text-green-700' : 'text-red-700'
              }`}>
                {selectedOption === currentDialogue.correctAnswer ? '✅ Doğru!' : '❌ Yanlış!'}
              </p>
              {currentDialogue.explanation && (
                <p className="text-sm text-gray-600 mt-1">{currentDialogue.explanation}</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Continue Button */}
      {showResult && (
        <Button
          onClick={handleNext}
          className="w-full py-4 text-lg font-bold bg-indigo-500 hover:bg-indigo-600 rounded-xl"
        >
          {isLastStep ? 'Sonuçları Gör' : 'Sonraki Diyalog'}
          <ChevronRight className="w-5 h-5 ml-2" />
        </Button>
      )}
    </div>
  );
}
